/**
 * The Signatures panel: every certificate signature in the document, with
 * what was checked and what (if anything) is wrong.
 */

import type { SignatureInfo, SignatureReport } from '../api'

interface Props {
  report: SignatureReport
  onGoTo: (pageIndex: number) => void
  onClose: () => void
}

const HEADLINE: Record<SignatureInfo['status'], string> = {
  valid: 'Valid',
  unknown: 'Signature is sound; signer not verified',
  invalid: 'Invalid',
}

function when(iso: string | null): string {
  if (!iso) return 'time not stated'
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString()
}

export function SignaturesDialog({ report, onGoTo, onClose }: Props) {
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="sigs-title">
      <div className="modal__panel sigs">
        <h2 className="modal__title" id="sigs-title">Signatures</h2>
        <div className="modal__body">
          {report.changedSince && (
            <p className="modal__hint modal__hint--warn">
              You have changed the document since it was signed. Saving those
              changes will invalidate the signatures below; Undo back to the signed
              version keeps them.
            </p>
          )}
          {report.signatures.length === 0 && <p>This document has no certificate signatures.</p>}
          <ol className="sigs__list">
            {report.signatures.map((sig, i) => (
              <li key={`${sig.field}:${i}`} className="sig" data-status={sig.status}>
                <div className="sig__head">
                  <span className="sig__badge" aria-hidden="true">
                    {sig.status === 'valid' ? '✔' : sig.status === 'unknown' ? '?' : '✖'}
                  </span>
                  <strong>{HEADLINE[sig.status]}</strong>
                  {sig.pageIndex !== null && (
                    <button className="linkbtn" onClick={() => onGoTo(sig.pageIndex!)}>
                      page {sig.pageIndex + 1}
                    </button>
                  )}
                </div>
                <div>
                  Signed by <strong>{sig.signer}</strong>{sig.email ? ` <${sig.email}>` : ''}, {when(sig.signedAt)}
                </div>
                <div className="sig__meta">
                  Certificate issued by {sig.issuer}
                  {sig.reason && <> &middot; Reason: {sig.reason}</>}
                  {sig.location && <> &middot; {sig.location}</>}
                </div>
                <ul className="sig__checks">
                  <li data-ok={sig.intact}>{sig.intact ? 'Unchanged since it was signed' : 'Altered after signing'}</li>
                  <li data-ok={sig.valid}>{sig.valid ? 'Signature verifies' : 'Signature does not verify'}</li>
                  <li data-ok={sig.trusted}>{sig.trusted ? 'Signer’s certificate is trusted' : 'Signer’s certificate is not from a trusted authority'}</li>
                  <li data-ok={sig.coverage !== 'changed'}>
                    {sig.coverage === 'entire' ? 'Covers the whole document'
                      : sig.coverage === 'allowed' ? 'Later changes are ones it allows (forms, further signatures)'
                        : 'Changed afterwards in a way it does not allow'}
                  </li>
                </ul>
                {sig.problems.length > 0 && (
                  <ul className="sig__problems">
                    {sig.problems.map((p) => <li key={p}>{p}</li>)}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        </div>
        <div className="modal__footer">
          <button className="btn btn--primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}
